import React from 'react'
import { useMutation } from '@apollo/client'
import { formatISO } from 'date-fns'
import { TestResult, EditTestResults, EditTestResultsVariables } from '@modmd/data'
import Button from 'components/Button'
import Card from 'components/Card'
import { Dialog } from 'components/Dialog'
import { DateInput } from 'components/DateInput'
import { pxToRem } from 'theme'
import { Box } from 'components/Layout'
import { EDIT_TEST_RESULTS, GET_TEST } from './operations'

interface Props {
  isOpen: boolean
  testId: string
  result?: TestResult | null
  date?: string | null
  onClose: () => void
  isReceivedDate?: boolean
  isProvisionedDate?: boolean
}

type DateField = {
  testedAt?: string | null
  resultReceivedAt?: string | null
  resultProvisionedAt?: string | null
}

export const ChangeDateDialog: React.VFC<Props> = ({
  isOpen,
  testId,
  date,
  onClose,
  isReceivedDate,
  isProvisionedDate,
}) => {
  const [selectedDate, setSelectedDate] = React.useState(date)
  const [editTestResults, editTestResultsData] = useMutation<
    EditTestResults,
    EditTestResultsVariables
  >(EDIT_TEST_RESULTS, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_TEST,
        variables: {
          id: testId,
        },
      },
    ],
  })

  const handleSave = async () => {
    try {
      let dateField: DateField = {}
      if (isReceivedDate) {
        dateField = { resultReceivedAt: selectedDate }
      } else if (isProvisionedDate) {
        dateField = { resultProvisionedAt: selectedDate }
      } else {
        dateField = { testedAt: selectedDate }
      }

      await editTestResults({
        variables: {
          inputData: {
            testIds: [testId],
            ...dateField,
          },
        },
      })
      onClose()
    } catch {
      // ignore error
    }
  }

  const getTitle = () => {
    if (isReceivedDate) return 'received date'
    if (isProvisionedDate) return 'provisioned date'
    return 'date'
  }

  const getLabel = () => {
    if (isReceivedDate) return 'Received Date'
    if (isProvisionedDate) return 'Provisioned Date'
    return 'Date'
  }

  return (
    <Dialog isOpen={isOpen} onDismiss={onClose} maxWidth={pxToRem(500)}>
      <Card.Title onDismiss={onClose}>Change {getTitle()}</Card.Title>
      <Card.Content>
        <Box height={pxToRem(250)}>
          <DateInput
            id="date"
            type="dateTime"
            label={getLabel()}
            popperPosition="bottom"
            value={selectedDate ? new Date(selectedDate) : null}
            onChange={(date) => {
              setSelectedDate(formatISO(date))
            }}
            isFullWidth
          />
        </Box>
      </Card.Content>
      <Card.Actions>
        <Button onClick={onClose} colorVariant="danger" appearance="ghost">
          Cancel
        </Button>
        <Button isFetching={editTestResultsData.loading} onClick={handleSave}>
          Save
        </Button>
      </Card.Actions>
    </Dialog>
  )
}
